import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { useDispatch, useSelector } from "react-redux";
import { fetchModules } from "@/redux/slice/module-slice";
import { fetchAllFeatures } from "@/redux/slice/all-features-slice";
import { fetchAllPermissions } from "@/redux/slice/all-permissions-slice";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { getUserPermissions, updateUserPermissions } from "@/api/new-api/permissions";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Save } from "lucide-react";

function StaffPermissionTab() {
  const dispatch = useDispatch();
  const { id: staffId } = useParams();

  const [moduleId, setModuleId] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [permModuleMap, setPermModuleMap] = useState({});

  const [userPermLoading, setUserPermLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Track if user permissions have been fetched at least once for this tab session
  const hasFetched = useRef(false);

  const { modules, loading: modulesLoading } = useSelector((state) => state.modules);
  const { features, loading: featuresLoading } = useSelector((state) => state.allFeatures);
  const { permissions, loading: permissionsLoading } = useSelector(
    (state) => state.allPermissions
  );

  const isBaseLoading = modulesLoading || featuresLoading || permissionsLoading;
  const isLoading = isBaseLoading || userPermLoading;

  useEffect(() => {
    if (!modules || modules.length === 0) dispatch(fetchModules());
    if (!features || features.length === 0) dispatch(fetchAllFeatures());
    if (!permissions || permissions.length === 0) dispatch(fetchAllPermissions());
  }, [dispatch]);

  // Filter modules that have at least one feature with permissions
  const validModules = useMemo(() => {
    if (!modules || modules.length === 0) return [];
    if (!features || features.length === 0) return [];
    if (!permissions || permissions.length === 0) return [];

    return modules.filter((mod) => {
      // Find features under this module
      const moduleFeatures = features.filter((f) => f?.module_id === mod?.id);
      if (moduleFeatures.length === 0) return false;

      // Check if any of these features have at least one permission
      return moduleFeatures.some((f) =>
        permissions.some((p) => p?.feature_id === f?.id)
      );
    });
  }, [modules, features, permissions]);

  // Set first valid module as default
  useEffect(() => {
    if (validModules?.length > 0) {
      if (!moduleId || !validModules.some((m) => m.id === moduleId)) {
        setModuleId(validModules[0]?.id);
      }
    }
  }, [validModules, moduleId]);

  useEffect(() => {
    if (hasFetched.current) return;
    if (!staffId) return;

    const fetchUserPerms = async () => {
      setUserPermLoading(true);
      try {
        const res = await getUserPermissions(staffId);
        if (res?.status === "success") {
          const userPerms = res?.data?.permissions || [];

          const checked = {};
          const modMap = {};

          const seen = new Set();
          userPerms.forEach((p) => {
            if (!seen.has(p.permission_id)) {
              seen.add(p.permission_id);
              checked[p.permission_id] = true;
              modMap[p.permission_id] = p.module_id;
            }
          });

          setCheckedItems(checked);
          setPermModuleMap(modMap);
          setIsDirty(false);
        } else {
          // toast.error(res?.message || "Failed to load user permissions");
        }
      } catch (err) {
        // toast.error("Something went wrong fetching permissions");
      } finally {
        setUserPermLoading(false);
        hasFetched.current = true;
      }
    };

    // Wait for base data to be ready before fetching user perms
    if (!isBaseLoading) {
      fetchUserPerms();
    }
  }, [staffId, isBaseLoading]);

  const filteredFeatures = useMemo(() => {
    if (!moduleId || !features) return [];
    return features.filter((f) => {
      if (f?.module_id !== moduleId) return false;
      return permissions?.some((p) => p?.feature_id === f?.id);
    });
  }, [features, permissions, moduleId]);

  // 4. Checkbox toggle
  const handleToggle = useCallback(
    (permId, moduleIdForPerm) => {
      setCheckedItems((prev) => {
        const next = { ...prev, [permId]: !prev[permId] };
        return next;
      });
      setPermModuleMap((prev) => ({
        ...prev,
        [permId]: moduleIdForPerm,
      }));
      setIsDirty(true);
    },
    []
  );


  const handleSave = async () => {
    setSaveLoading(true);
    try {
      // Build permissions array: only those that are checked=true
      const permissionsPayload = Object.entries(checkedItems)
        .filter(([, isChecked]) => isChecked)
        .map(([permIdStr]) => {
          const permId = parseInt(permIdStr, 10);
          const moduleIdForPerm =
            permModuleMap[permId] ??
            permissions?.find((p) => p.id === permId)?.module_id;

          return {
            module_id: moduleIdForPerm,
            permission_id: permId,
          };
        })
        .filter((p) => p.module_id != null); // safety filter

      const body = {
        user_id: parseInt(staffId, 10),
        permissions: permissionsPayload,
      };

      const res = await updateUserPermissions(body);
      if (res?.status === "success") {
        toast.success("Permissions updated successfully!");
        setIsDirty(false);
      } else {
        toast.error(res?.message || "Failed to update permissions");
      }
    } catch (err) {
      toast.error("Something went wrong while saving");
    } finally {
      setSaveLoading(false);
    }
  };


  return (
    <TabsContent className="mt-2" value="permission">
      <div className="rounded-md overflow-hidden">
        {/* Top bar: module tabs + save button */}
        <div className="flex items-center justify-between gap-3 bg-[#2D2E2F] pr-3">
          {/* Module Tab Bar */}
          <div className="flex gap-1.5 items-center overflow-x-auto custom-scrollbar flex-1">
            {isLoading ? (
              <div className="flex gap-1.5 px-4 py-2">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="h-9 w-28 bg-neutral-700 animate-pulse rounded"
                  />
                ))}
              </div>
            ) : validModules.length === 0 ? (
              <div className="px-4 py-2 text-xs text-neutral-400">
                No modules with permissions found.
              </div>
            ) : (
              validModules.map((module) => (
                <div
                  key={module?.id}
                  onClick={() => setModuleId(module?.id)}
                  className={`${
                    module?.id === moduleId
                      ? "bg-cyellow !text-black font-medium rounded"
                      : "text-cwhite"
                  } py-2 px-4 bg-[#1F1F1F] flex-shrink-0 cursor-pointer text-sm transition-colors duration-150`}
                >
                  {module?.module_name}
                </div>
              ))
            )}
          </div>

          {isDirty && (
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saveLoading}
              className="bg-cgreen hover:bg-cgreen/90 text-black font-semibold flex-shrink-0 gap-1.5"
            >
              <Save size={14} />
              {saveLoading ? "Saving..." : "Save"}
            </Button>
          )}
        </div>

        {/* Features & Permissions Table */}
        <div className="w-full overflow-x-auto">
          {/* Header */}
          <div className="flex bg-[#1c1c1c] text-cgreen font-semibold border-b border-neutral-700 px-4 py-2 min-w-[500px]">
            <div className="min-w-[240px] text-sm">Features</div>
            <div className="flex-1 text-sm">Permissions</div>
          </div>

          {/* Body */}
          <div className="min-w-[500px]">
            {isLoading ? (
              <div className="flex flex-col">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center border-b border-neutral-700 px-4 py-3 gap-4"
                  >
                    <div className="h-4 w-40 bg-neutral-700 animate-pulse rounded min-w-[240px]" />
                    <div className="flex gap-5 flex-1">
                      {[...Array(3)].map((_, j) => (
                        <div
                          key={j}
                          className="h-4 w-24 bg-neutral-700 animate-pulse rounded"
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredFeatures.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-sm text-cwhite3">
                No features available for this module.
              </div>
            ) : (
              filteredFeatures.map((feature) => {
                // allPermissions from redux, filtered by feature
                const featurePermissions = permissions?.filter(
                  (perm) => perm?.feature_id === feature?.id
                );

                return (
                  <div
                    key={feature?.id}
                    className="flex items-start border-b border-neutral-700 px-4 py-3"
                  >
                    {/* Feature Name */}
                    <div className="min-w-[240px] text-cwhite2 text-sm font-normal pt-1.5">
                      {feature?.feature_name}
                    </div>

                    {/* Permissions with checkboxes */}
                    <div className="flex flex-wrap gap-4 items-center flex-1 pr-4">
                      {featurePermissions?.length > 0 ? (
                        featurePermissions.map((perm) => (
                          <div
                            key={perm?.id}
                            className="flex items-center gap-2 py-1.5"
                          >
                            <Checkbox
                              id={`staff-perm-${perm?.id}`}
                              checked={!!checkedItems[perm?.id]}
                              onCheckedChange={() =>
                                handleToggle(perm?.id, perm?.module_id ?? moduleId)
                              }
                            />
                            <label
                              htmlFor={`staff-perm-${perm?.id}`}
                              className="text-sm text-cwhite3 flex-shrink-0 cursor-pointer select-none"
                            >
                              {perm?.permission_name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <span className="text-xs text-cwhite3 italic">
                          No permissions
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </TabsContent>
  );
}

export default StaffPermissionTab;
